import { create } from "zustand";
import { persist } from "zustand/middleware";
import { useEffect } from "react";
import { userApi, type UpdateUserRequest } from "~/api/user";
import { CLIENT_CONFIG } from "~/config/client";
import type { User } from "~/types/user";
import { devLog } from "~/lib/dev-log";
import { pushToDataLayer, createUserDataForGTM, createSignUpEventForGTM } from "~/lib/gtm";

interface UserState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  error: string | null;
  userDataReloaded: boolean;

  // Auth actions (no reload)
  createAccount: () => Promise<void>;
  tokenLogin: (token: string) => Promise<void>;
  initializeFromStorage: () => Promise<void>;

  // Auth actions (reload)
  signup: (email: string) => Promise<"authenticated" | "email-sent" | void>;
  finishDelayedSignup: (email: string) => Promise<"authenticated" | "email-sent" | void>;
  emailLogin: (email: string, otpToken: string) => Promise<"authenticated" | void>;
  firebaseLogin: (idToken: string, email?: string, provider?: "facebook" | "google") => Promise<"authenticated" | void>;
  facebookLogin: (token: string, profile: string | object, type?: string) => Promise<"authenticated" | void>;
  logout: () => void;

  // User data actions
  updateUser: (data: UpdateUserRequest) => Promise<void>;

  // User data refresh
  getData: () => Promise<User>;

  // Utility actions
  clearError: () => void;

  // Atomic operation to check and set loading state
  trySetLoading: (reload?: boolean) => boolean;
  setNotLoading: () => void;
}

export const useUserStore = create<UserState>()(
  persist(
    (set, get) => {
      // ============================================================================
      // Helper Functions
      // ============================================================================

      /**
       * Pushes user ID to Google Tag Manager using the generic GTM utility
       */
      const pushUserIdToDataLayer = (userId: string | null) => {
        const userData = createUserDataForGTM(userId);
        pushToDataLayer(userData);
      };

      /**
       * Pushes sign_up event to Google Tag Manager
       */
      const pushSignUpEvent = (signupMethod: string) => {
        const signUpData = createSignUpEventForGTM(signupMethod);
        pushToDataLayer(signUpData);
      };

      return {
        user: null,
        token: null,
        isLoading: false,
        error: null,
        userDataReloaded: false,

        signup: async (email: string) => {
          if (!get().trySetLoading()) {
            devLog("Signup skipped - already loading");
            return;
          }
          try {
            const response = await userApi.signup({
              email,
              termsVersion: CLIENT_CONFIG.TERMS_VERSION,
            });
            set({ isLoading: false });

            if ("user" in response && "token" in response) {
              set({ user: response.user, token: response.token });
              pushUserIdToDataLayer(response.user._id);
              pushSignUpEvent("email");
              return "authenticated";
            }
            if (response.type === "success" && response.status === 202) {
              return "email-sent";
            }
          } catch (error) {
            console.error("❌ Signup failed:", error);
            set({
              error: error instanceof Error ? error.message : "Failed to signup",
              isLoading: false,
            });
            throw error;
          }
        },

        finishDelayedSignup: async (email: string) => {
          if (!get().trySetLoading()) {
            devLog("Finish delayed signup skipped - already loading");
            return;
          }
          try {
            const response = await userApi.finishDelayedSignup({
              email,
              termsVersion: CLIENT_CONFIG.TERMS_VERSION,
            });
            set({ isLoading: false });

            if ("user" in response && "token" in response) {
              set({ user: response.user, token: response.token });
              pushUserIdToDataLayer(response.user._id);
              pushSignUpEvent("email");
              return "authenticated";
            }
            if (response.type === "success" && response.status === 202) {
              return "email-sent";
            }
          } catch (error) {
            console.error("❌ Finish delayed signup failed:", error);
            set({
              error: error instanceof Error ? error.message : "Failed to finish delayed signup",
              isLoading: false,
            });
            throw error;
          }
        },

        emailLogin: async (email: string, otpToken: string) => {
          if (!get().trySetLoading()) {
            devLog("Email login skipped - already loading");
            return;
          }
          try {
            const response = await userApi.emailLogin({
              email,
              token: otpToken,
              termsVersion: CLIENT_CONFIG.TERMS_VERSION,
            });
            set({ isLoading: false });

            if ("user" in response && "token" in response) {
              set({ user: response.user, token: response.token });
              pushUserIdToDataLayer(response.user._id);
              pushSignUpEvent("email");
              return "authenticated";
            }
          } catch (error) {
            console.error("❌ Email login failed:", error);
            set({
              error: error instanceof Error ? error.message : "Failed to login",
              isLoading: false,
            });
            throw error;
          }
        },

        firebaseLogin: async (idToken: string, email?: string, provider?: "facebook" | "google") => {
          if (!get().trySetLoading()) {
            devLog("Firebase login skipped - already loading");
            return;
          }
          try {
            const response = await userApi.firebaseLogin({ idToken, email });
            set({ isLoading: false });

            if ("user" in response && "token" in response) {
              set({ user: response.user, token: response.token });
              pushUserIdToDataLayer(response.user._id);
              pushSignUpEvent(provider || "unknown_provider");
              return "authenticated";
            }
          } catch (error) {
            console.error("❌ Firebase login failed:", error);
            set({
              error: error instanceof Error ? error.message : `Failed to login with ${provider}`,
              isLoading: false,
            });
            throw error;
          }
        },

        facebookLogin: async (token: string, profile: string | object, type?: string) => {
          if (!get().trySetLoading()) {
            devLog("Facebook login skipped - already loading");
            return;
          }
          try {
            const response = await userApi.facebookLogin({ token, profile, type });
            set({ isLoading: false });

            if ("user" in response && "token" in response) {
              set({ user: response.user, token: response.token });
              pushUserIdToDataLayer(response.user._id);
              pushSignUpEvent("facebook");
              return "authenticated";
            }
          } catch (error) {
            set({ isLoading: false });
            console.error("Facebook login failed:", error);
            throw error;
          }
        },

        createAccount: async () => {
          // Use atomic check-and-set
          if (!get().trySetLoading(false)) {
            devLog("🚫 Create account skipped - already loading");
            return;
          }

          try {
            devLog("🔄 Creating account with terms version:", CLIENT_CONFIG.TERMS_VERSION);

            const response = await userApi.createAccount({
              termsVersion: CLIENT_CONFIG.TERMS_VERSION,
            });

            devLog("✅ Account created successfully:", response);

            set({
              user: response.user,
              token: response.token,
              isLoading: false,
              userDataReloaded: true,
            });
            pushUserIdToDataLayer(response.user._id);
            pushSignUpEvent("anonymous");
          } catch (error) {
            console.error("❌ Account creation failed:", error);
            set({
              error: error instanceof Error ? error.message : "Failed to create account",
              isLoading: false,
            });
            throw error;
          }
        },

        tokenLogin: async (token: string) => {
          // Use atomic check-and-set
          if (!get().trySetLoading(false)) {
            devLog("🚫 Token login skipped - already loading");
            return;
          }

          try {
            devLog("🔄 Attempting token login");

            const response = await userApi.tokenLogin(token);

            devLog("✅ Token login successful:", response);

            set({
              user: response.user,
              token: response.token,
              isLoading: false,
              userDataReloaded: true,
            });
            pushUserIdToDataLayer(response.user._id);
            pushSignUpEvent("token");
          } catch (error) {
            console.error("❌ Token login failed:", error);
            // Clear invalid token from storage
            set({
              user: null,
              token: null,
              userDataReloaded: false,
              error: error instanceof Error ? error.message : "Failed to authenticate with token",
              isLoading: false,
            });

            pushUserIdToDataLayer(null);

            throw error;
          }
        },

        initializeFromStorage: async () => {
          // Use atomic check-and-set
          if (!get().trySetLoading(false)) {
            devLog("🚫 Initialize from storage skipped - already loading");
            return;
          }

          const state = get();

          try {
            // If we have a token in storage, always try to authenticate with it
            // This ensures we get fresh user data from the server on new browser sessions
            if (state.token) {
              try {
                // Clear loading state before calling tokenLogin since it will set its own loading state
                get().setNotLoading();
                await state.tokenLogin(state.token);
              } catch (_error) {
                devLog("🔄 Token authentication failed, creating new account");
                // If token login fails, create a new account
                // Clear loading state before calling createAccount since it will set its own loading state
                get().setNotLoading();
                await state.createAccount();
              }
            } else {
              devLog("🔄 No token found, creating new account");
              // No token, create a new account
              // Clear loading state before calling createAccount since it will set its own loading state
              get().setNotLoading();
              await state.createAccount();
            }
          } catch (error) {
            // If something goes wrong, ensure loading state is cleared
            set({
              error: error instanceof Error ? error.message : "Failed to initialize user",
              isLoading: false,
            });
            throw error;
          }
        },

        logout: () => {
          set({ user: null, token: null, userDataReloaded: false, error: null });
          pushUserIdToDataLayer(null);
          window.location.href = "/"; // Basically reload the page, so that it can trigger the "create_account" API
        },

        updateUser: async (data: UpdateUserRequest) => {
          if (!get().trySetLoading(true)) {
            devLog("🚫 Update user skipped - already loading");
            return;
          }
          try {
            const response = await userApi.updateUser(data);
            set({ user: response });
            devLog("✅ User updated successfully:", response);
          } catch (error) {
            console.error("❌ User update failed:", error);
            set({
              error: error instanceof Error ? error.message : "Failed to update user",
            });
            throw error;
          }
        },

        getData: async () => {
          if (!get().trySetLoading(true)) {
            devLog("🚫 Get data skipped - already loading");
            return get().user!;
          }
          try {
            const response = await userApi.getData();
            set({ user: response, isLoading: false, userDataReloaded: true });
            devLog("✅ User data fetched successfully:", response);
            return response;
          } catch (error) {
            console.error("❌ Failed to fetch user data:", error);
            set({
              error: error instanceof Error ? error.message : "Failed to fetch user data",
              isLoading: false,
            });
            throw error;
          }
        },

        clearError: () => set({ error: null }),

        // Atomic check-and-set operation for loading state
        trySetLoading: (reload = true) => {
          const state = get();
          if (state.isLoading) {
            return false; // Already loading, operation rejected
          }
          if (state.userDataReloaded && !reload) {
            return false; // Already loaded, operation rejected
          }
          set({ isLoading: true, error: null, userDataReloaded: reload });
          return true; // Successfully set to loading
        },

        setNotLoading: () => set({ isLoading: false }),
      };
    },
    {
      name: "user-storage",
      partialize: state => ({
        user: state.user,
        token: state.token,
      }),
    }
  )
);

/**
 * Hook to initialize user authentication on app start
 * Call this in your root component or app initialization
 */
export const useInitializeUser = () => {
  const { initializeFromStorage, isLoading, error } = useUserStore();

  useEffect(() => {
    initializeFromStorage().catch(console.error);
  }, [initializeFromStorage]);

  return { isLoading, error };
};
